import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Sparkles } from "lucide-react";
import { BRAND, HERO_IMAGE } from "@/data/content";
import { DownloadButtons } from "@/sections/DownloadButtons";
import { useLang } from "@/i18n/LanguageContext";

export const Hero = () => {
    const { t, lang } = useLang();
    const Arrow = lang === "ar" ? ArrowLeft : ArrowRight;

    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative min-h-[100vh] overflow-hidden grain"
        >
            <div className="absolute inset-0">
                <img
                    src={HERO_IMAGE}
                    alt=""
                    className="w-full h-full object-cover opacity-60"
                />
                <div className="absolute inset-0 bg-gradient-to-l from-black via-black/60 to-black/85" />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg)] via-transparent to-black/40" />
            </div>

            <div className="absolute -left-20 top-1/4 w-[60%] h-[2px] bg-[var(--primary)] -rotate-12 opacity-40" />
            <div className="absolute -left-20 top-1/4 mt-3 w-[40%] h-[2px] bg-[var(--primary)] -rotate-12 opacity-20" />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-32 sm:pt-40 pb-20 flex flex-col lg:flex-row items-center gap-12">
                <div className="flex-1 w-full">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[var(--primary)]/30 bg-[var(--primary)]/10 mb-6"
                    >
                        <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--primary)] pulse-dot" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-[var(--primary)]" />
                        </span>
                        <span className="text-xs sm:text-sm text-[var(--primary)] font-bold">
                            {t.hero.badge}
                        </span>
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black leading-[1.05] tracking-tight"
                    >
                        {t.hero.h1Top}
                        <br />
                        <span className="text-gradient-orange">{t.hero.h1Bottom}</span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.25 }}
                        className="mt-6 text-base sm:text-xl text-neutral-300 max-w-xl leading-relaxed"
                    >
                        {t.hero.sub}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className="mt-10"
                    >
                        <DownloadButtons testIdPrefix="hero-" />
                    </motion.div>

                    <motion.a
                        href="#features"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.7 }}
                        data-testid="hero-learn-more"
                        className="mt-8 inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-[var(--primary)] transition-colors group"
                    >
                        {t.hero.learnMore}
                        <Arrow className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                    </motion.a>
                </div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    whileHover={{ scale: 1.03, rotate: -1 }}
                    className="relative flex-shrink-0 w-64 sm:w-80 lg:w-96 cursor-pointer group"
                    data-testid="hero-logo-card"
                >
                    {/* Outer breathing halo */}
                    <div className="absolute -inset-10 bg-[radial-gradient(circle,rgba(255,90,0,0.5)_0%,transparent_70%)] blur-3xl breathe pointer-events-none" />

                    {/* Rotating conic-gradient ring */}
                    <div className="absolute inset-0 rounded-3xl overflow-hidden pointer-events-none">
                        <div
                            className="absolute -inset-[50%] spin-slow"
                            style={{
                                background:
                                    "conic-gradient(from 0deg, transparent 0deg, #ff5a00 40deg, transparent 80deg, transparent 180deg, #ff7326 220deg, transparent 260deg)",
                            }}
                        />
                    </div>

                    {/* Inner card surface */}
                    <div className="absolute inset-[3px] rounded-3xl bg-black overflow-hidden">
                        {/* Layered orange gradient mesh */}
                        <div className="absolute inset-0 bg-[radial-gradient(at_top_left,rgba(255,115,38,0.55)_0%,transparent_55%),radial-gradient(at_bottom_right,rgba(255,59,48,0.25)_0%,transparent_60%),linear-gradient(135deg,#1a0d04_0%,#050505_100%)]" />
                        {/* Grain + stripes */}
                        <div className="absolute inset-0 opacity-25 mix-blend-overlay grain" />
                        <div className="absolute inset-0 diagonal-stripes opacity-50" />

                        {/* Status bar (top) */}
                        <div className="absolute top-3 right-4 left-4 flex items-center justify-between text-xs z-10">
                            <span className="flex items-center gap-1.5 text-white/90 font-bold">
                                <Sparkles className="w-3 h-3 text-[var(--primary)]" />
                                <span>{t.hero.aiSupported}</span>
                            </span>
                            <span className="font-display text-[var(--primary)] tracking-wider">
                                v1.0
                            </span>
                        </div>

                        {/* Corner accents */}
                        <div className="absolute top-3 right-3 w-5 h-5 border-t-2 border-r-2 border-[var(--primary)]" />
                        <div className="absolute top-3 left-3 w-5 h-5 border-t-2 border-l-2 border-[var(--primary)]" />
                        <div className="absolute bottom-3 right-3 w-5 h-5 border-b-2 border-r-2 border-[var(--primary)]" />
                        <div className="absolute bottom-3 left-3 w-5 h-5 border-b-2 border-l-2 border-[var(--primary)]" />
                    </div>

                    {/* Aspect-square wrapper holds layout; icon overlaid above all backgrounds */}
                    <div className="relative aspect-square">
                        {/* The orange icon — full visible, gentle idle motion */}
                        <img
                            src={BRAND.icon}
                            alt="606FITNESS Icon"
                            className="absolute inset-6 w-[calc(100%-3rem)] h-[calc(100%-3rem)] object-contain icon-idle drop-shadow-[0_0_24px_rgba(255,90,0,0.6)]"
                            style={{ mixBlendMode: "screen" }}
                        />
                    </div>
                </motion.div>
            </div>

            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2 text-neutral-500">
                <span className="text-[10px] uppercase tracking-widest">
                    {t.hero.scroll}
                </span>
                <div className="w-[1px] h-10 bg-gradient-to-b from-[var(--primary)] to-transparent" />
            </div>
        </section>
    );
};
